import React, { useState, useRef, useEffect } from "react";
import { Button, Form, message, notification, Tooltip, Radio } from "antd";
import dayjs from "dayjs";
import { EditOutlined } from "@ant-design/icons";
import "./style.css";
import GenericDocumentManagerTable from "../../../Components/Tables/GenericDocumentManagerTable";
import InputCard from "../../../Components/Cards/InputCard";
import { useNavigate } from "react-router-dom";
import ModuleSelctorCards from "../../../Components/Cards/ModuleSelectorCards";
import FuelIcon from "../../../Assets/Icons/fuel-icon.svg";
import ClipboardIcon from "../../../Assets/Icons/clipboard.svg";
import CarPartsIcon from "../../../Assets/Icons/car_repair.svg";
import logoCompany from "../../../Assets/Icons/companylogo.svg";
import pdfFile from "../../../Assets/Files/file-sample.pdf";
import docxFile from "../../../Assets/Files/file-sample.docx";
import { useLocation } from "react-router-dom";
import {
  getCompanyDetails,
  updateCompanyDetails,
  deleteCompanyDocument,
} from "../../../API/FleetManager/CompanyDetails";
import { deleteDocument } from "../../../API/Documents";
import { getModules, getDocumentCategories } from "../../../API/Config";
import usePageAccess from "../../../Utlis/usePageAccess";
import { navixyApiKeyFieldProps } from "../../../Utlis/NavixyApiKeys";

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL?.replace(
  /\/?api\/?$/,
  "",
);

const getErrorMessage = (data, fallback) => {
  if (!data) return fallback;
  if (data.message) return data.message;
  if (data.detail) return data.detail;

  const fieldErrors = Object.values(data)
    .flat()
    .filter((msg) => typeof msg === "string");

  return fieldErrors.length ? fieldErrors.join(" ") : fallback;
};

const resolveAvatarUrl = (avatar) => {
  if (!avatar) return null;
  if (/^https?:\/\//i.test(avatar) || avatar.startsWith("blob:")) {
    return avatar;
  }
  return `${BACKEND_ORIGIN}${avatar.startsWith("/") ? "" : "/"}${avatar}`;
};

const formatDate = (value) =>
  value
    ? new Date(value).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "-";

const mapDocument = (doc) => ({
  key: String(doc.id),
  id: doc.id,
  name: doc.file ? doc.file.split("/").pop() : "-",
  category: doc.category || "-",
  allowedFormats: doc.allowed_formats || [],
  date: formatDate(doc.uploaded_at),
  expiryDate: formatDate(doc.expiry_date),
  fileUrl: resolveAvatarUrl(doc.file),
});

const PRICING_TABS = ["Monthly Pricing", "Annually Pricing"];

const cycleToPeriod = (cycle) =>
  cycle === "annual"
    ? "Annually Pricing"
    : cycle === "monthly"
      ? "Monthly Pricing"
      : null;

const CompanyDetail = () => {
  const { canEdit } = usePageAccess("company-details");
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [avatar, setAvatar] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null);
  const [vehicles, setVehicles] = useState(0);
  const [pricingPeriod, setPricingPeriod] = useState(PRICING_TABS[0]);
  const [catalogModules, setCatalogModules] = useState([]);
  const [documentCategories, setDocumentCategories] = useState([]);
  const location = useLocation();

  const [existingData, setExistingData] = useState(
    location.state?.userInfo || null,
  );
  const [isEditing, setIsEditing] = useState(
    canEdit && !!location.state?.userInfo,
  );

  useEffect(() => {
    if (location.state?.userInfo) return;

    const fetchProfile = async () => {
      try {
        const response = await getCompanyDetails();

        if (Math.floor(response?.status / 100) === 2) {
          const company = response.data?.data || {};

          setExistingData({
            legal_name: company.legal_name,
            registration_number: company.registration_number,
            registration_date: company.registration_date,
            contact_email: company.contact_email,
            contact_phone: company.contact_phone,
            registration_contact_phone: company.registration_contact_phone,
            address: company.address,
            currency: company.currency || "JMD",
            date_incorporated: company.date_incorporated,
            avatar: resolveAvatarUrl(company.avatar),
            primary_contact_person: company.primary_contact_person,
            designation: company.designation,
            secondary_contact_person: company.secondary_contact_person,
            secondary_designation: company.secondary_designation,
            secondary_contact_email: company.secondary_contact_email,
            secondary_contact_phone: company.secondary_contact_phone,
            assigned_modules: company.assigned_modules,
            vehicle_limit: company.vehicle_limit,
            documents: company.documents,
            navixy_api_keys: company.navixy_api_keys || [],
          });
        } else {
          setIsEditing(true);
        }
      } catch (err) {
        setIsEditing(true);
      }
    };

    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchModules = async () => {
      const res = await getModules();
      const modules = Array.isArray(res?.data)
        ? res.data
        : res?.data?.data || res?.data?.results || [];

      setCatalogModules(modules);
    };

    fetchModules();
  }, []);

  useEffect(() => {
    const fetchDocumentCategories = async () => {
      const res = await getDocumentCategories("fleet-manager");
      const list = res?.data?.data;
      if (res?.status === 200 && Array.isArray(list)) {
        setDocumentCategories(
          list.map((cat) => ({
            label: cat.document_name,
            value: cat.document_name,
            allowedFormats: cat.allowed_formats,
          })),
        );
      }
    };

    fetchDocumentCategories();
  }, []);

  useEffect(() => {
    if (!existingData) return;

    form.setFieldsValue({
      legalBusinessName: existingData.legal_name,
      registrationNumber: existingData.registration_number,
      registrationDate: existingData.registration_date
        ? dayjs(existingData.registration_date)
        : undefined,
      contactNumber: existingData.registration_contact_phone,
      currency: existingData.currency,
      headQuarterAddress: existingData.address,
      apiKeys: existingData.navixy_api_keys || [],
      dateIncorporated: existingData.date_incorporated
        ? dayjs(existingData.date_incorporated)
        : undefined,
      primarycontactperson: existingData.primary_contact_person,
      designation: existingData.designation,
      businessemail: existingData.contact_email,
      phonenumber: existingData.contact_phone,
      secondarycontactperson: existingData.secondary_contact_person,
      secondaryDesignation: existingData.secondary_designation,
      secondaryEmail: existingData.secondary_contact_email,
      secondaryPhone: existingData.secondary_contact_phone,
    });

    setVehicles(existingData.vehicle_limit || 0);
    setDocuments((existingData.documents || []).map(mapDocument));
  }, [existingData]);

  useEffect(() => {
    if (existingData?.avatar) {
      setAvatar(existingData.avatar);
    }
  }, [existingData]);

  const fileInputRef = useRef(null);

  const handleButtonClick = () => {
    fileInputRef.current.click();
  };

  const MAX_FILENAME_LENGTH = 100;

  const shortenFileName = (file, maxLength = MAX_FILENAME_LENGTH) => {
    if (file.name.length <= maxLength) return file;

    const dotIndex = file.name.lastIndexOf(".");
    const ext = dotIndex !== -1 ? file.name.slice(dotIndex) : "";
    const base = dotIndex !== -1 ? file.name.slice(0, dotIndex) : file.name;
    const truncatedBase = base.slice(0, Math.max(1, maxLength - ext.length));

    return new File([file], `${truncatedBase}${ext}`, { type: file.type });
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const shortenedFile = shortenFileName(file);
    const previewUrl = URL.createObjectURL(shortenedFile);
    setAvatar(previewUrl);

    if (!existingData) return;

    try {
      const formData = new FormData();
      formData.append("avatar", shortenedFile);

      const response = await updateCompanyDetails(formData);

      if (Math.floor(response?.status / 100) === 2) {
        const updated = response.data?.data;
        setAvatar(resolveAvatarUrl(updated?.avatar) || previewUrl);
        message.success(
          response?.data?.message || "Avatar updated successfully",
        );
      } else {
        message.error(
          getErrorMessage(response?.data, "Unexpected response from server."),
        );
      }
    } catch (err) {
      message.error(
        getErrorMessage(err?.response?.data, "Failed to update avatar."),
      );
    }
  };

  const [documents, setDocuments] = useState([]);

  const registrationInformation = [
    {
      name: "legalBusinessName",
      label: "Legal Business Name",
      fullWidth: false,
      type: "text",
      placeholder: "Enter Legal Business Name",
      rules: [{ required: true, message: "Business name is required" }],
    },
    {
      name: "registrationNumber",
      label: "Registration Number",
      placeholder: "Enter Registration Number",
      fullWidth: false,
      type: "text",
      rules: [{ required: true, message: "Registration number is required" }],
    },
    {
      name: "registrationDate",
      label: "Registration Date",
      placeholder:
        existingData && !isEditing ? "-" : "Select Registration Date",
      fullWidth: false,
      type: "date",
    },
    {
      name: "contactNumber",
      label: "Company Contact Number",
      placeholder: "Enter Contact Number",
      fullWidth: false,
      type: "phone",
      rules: [
        { required: true, message: "Contact number is required" },
        {
          validator: (_, value) => {
            if (!value || value.length >= 10) {
              return Promise.resolve();
            }
            return Promise.reject("Enter valid phone number");
          },
        },
      ],
    },
    {
      name: "currency",
      label: "Currency",
      placeholder: "Enter amount",
      fullWidth: false,
      type: "currency",
      rules: [{ required: true, message: "Currency is required" }],
    },
    {
      name: "headQuarterAddress",
      label: "Headquarters Address",
      placeholder: "Enter Address Of Headquarters",
      fullWidth: true,
      type: "textarea",
      rules: [{ required: true, message: "Address is required" }],
    },
    {
      name: "dateIncorporated",
      label: "Date Incorporated",
      placeholder:
        existingData && !isEditing ? "-" : "Select Date Incorporated",
      type: "date",
      rules: [{ required: true, message: "Date incorporated is required" }],
    },
    {
      name: "apiKeys",
      label: "API Key",
      placeholder: "Enter API key",
      type: "multi-input",
      addLabel: "Add another API key",
      ...navixyApiKeyFieldProps,
      fullWidth: true,
    },
  ];

  const contactInfoFields = [
    {
      name: "primarycontactperson",
      label: "Primary Contact Person",
      placeholder: "Enter Name Of Primary Contact Person",
      type: "text",
      rules: [{ required: true, message: "Name is required" }],
    },
    {
      name: "designation",
      label: "Designation",
      placeholder: "Enter Designation Of Primary Contact Person",
      type: "text",
      rules: [{ required: true, message: "Designation is required" }],
    },
    {
      name: "businessemail",
      label: "Business Email",
      placeholder: "Enter Email Of Primary Contact Person",
      type: "text",
      rules: [
        { required: true, message: "Email is required" },
        { type: "email", message: "Enter valid email" },
      ],
    },
    {
      name: "phonenumber",
      label: "Phone Number",
      placeholder: "Enter Contact Number Of Primary Contact Person",
      type: "phone",
      rules: [
        { required: true, message: "Phone number is required" },
        {
          validator: (_, value) => {
            if (!value || value.length >= 10) {
              return Promise.resolve();
            }
            return Promise.reject("Enter valid phone number");
          },
        },
      ],
    },
  ];

  const secondaryContactInformationFields = [
    {
      name: "secondarycontactperson",
      label: "Secondary Contact Person",
      placeholder: "Enter Name of Secondary Contact Person",
      type: "text",
      rules: [{ required: true, message: "Name is required" }],
    },
    {
      name: "secondaryDesignation",
      label: "Designation",
      placeholder: "Enter Designation of Secondary Contact Person",
      type: "text",
      rules: [{ required: true, message: "Designation is required" }],
    },
    {
      name: "secondaryEmail",
      label: "Business Email",
      placeholder: "Enter Buisness Email Of Secondary Contact Person",
      type: "text",
      rules: [
        { required: true, message: "Email is required" },
        { type: "email", message: "Enter valid email" },
      ],
    },
    {
      name: "secondaryPhone",
      label: "Phone Number",
      placeholder: "Enter Contact Number of Secondary Contact Person",
      type: "phone",
      rules: [{ required: true, message: "Phone is required" }],
    },
  ];

  const MODULE_ICONS_BY_CODE = {
    fuel: FuelIcon,
    maintenance: ClipboardIcon,
    parts: CarPartsIcon,
  };

  const planPricingPeriod = cycleToPeriod(
    existingData?.assigned_modules?.[0]?.billing_cycle,
  );

  useEffect(() => {
    if (planPricingPeriod) setPricingPeriod(planPricingPeriod);
  }, [planPricingPeriod]);

  const isAnnual = pricingPeriod === "Annually Pricing";

  const buildModuleOption = (mod) => {
    const fallbackIcon =
      MODULE_ICONS_BY_CODE[mod.code?.toLowerCase()] || ClipboardIcon;
    const iconSrc = mod.icon ? resolveAvatarUrl(mod.icon) : fallbackIcon;
    const isInactive = mod.status && mod.status !== "active";
    const rawPrice = isAnnual
      ? mod.annual_price_per_vehicle
      : mod.monthly_price_per_vehicle;
    const price = parseFloat(rawPrice);

    return {
      id: mod.id,
      title: mod.name,
      description: mod.description,
      price: rawPrice
        ? price
          ? `${mod.currency || "JMD"} ${price.toFixed(2)}/vehicle`
          : "Free"
        : undefined,
      icon: <img src={iconSrc} alt={mod.name} />,
      disabled: isInactive,
      tag: isInactive ? "Coming Soon" : undefined,
    };
  };

  const assignedModuleOptions = (existingData?.assigned_modules || []).map(
    buildModuleOption,
  );
  const catalogModuleOptions = catalogModules.map(buildModuleOption);

  const moduleOptions = !existingData
    ? catalogModuleOptions
    : assignedModuleOptions;

  const handleSelection = (item) => {
    setSelectedModule(item);
    console.log("Selected Module:", item);
  };

  const handleCancel = () => {
    if (!existingData) {
      form.resetFields();
      setAvatar(null);
      setSelectedModule(null);
      setVehicles(0);
      setDocuments([]);
      return;
    }

    form.setFieldsValue({
      legalBusinessName: existingData.legal_name,
      registrationNumber: existingData.registration_number,
      registrationDate: existingData.registration_date
        ? dayjs(existingData.registration_date)
        : undefined,
      contactNumber: existingData.registration_contact_phone,
      currency: existingData.currency,
      headQuarterAddress: existingData.address,
      apiKeys: existingData.navixy_api_keys || [],
      dateIncorporated: existingData.date_incorporated
        ? dayjs(existingData.date_incorporated)
        : undefined,
      primarycontactperson: existingData.primary_contact_person,
      designation: existingData.designation,
      businessemail: existingData.contact_email,
      phonenumber: existingData.contact_phone,
      secondarycontactperson: existingData.secondary_contact_person,
      secondaryDesignation: existingData.secondary_designation,
      secondaryEmail: existingData.secondary_contact_email,
      secondaryPhone: existingData.secondary_contact_phone,
    });

    setVehicles(existingData.vehicle_limit || 0);
    setDocuments((existingData.documents || []).map(mapDocument));

    if (existingData?.avatar) {
      setAvatar(existingData.avatar);
    }

    setIsEditing(false);
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSubmit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (error) {
      return message.error("Please check the form for missing fields.");
    }

    if (!existingData) {
      if (!selectedModule) return message.error("Please select a module.");
      if (!documents || documents.length === 0)
        return message.error("Please upload at least one document.");
      if (!vehicles || vehicles < 1)
        return message.error("Please enter number of vehicles.");
    }

    try {
      const formData = new FormData();
      formData.append("legal_name", values.legalBusinessName);
      formData.append("registration_number", values.registrationNumber);
      formData.append("contact_email", values.businessemail);
      formData.append("registration_contact_phone", values.contactNumber);
      formData.append("contact_phone", values.phonenumber);
      formData.append("address", values.headQuarterAddress);
      formData.append("currency", values.currency);
      formData.append("navixy_api_keys", JSON.stringify(values.apiKeys || []));
      formData.append(
        "date_incorporated",
        values.dateIncorporated ? values.dateIncorporated.format("YYYY-MM-DD") : "",
      );
      formData.append("primary_contact_person", values.primarycontactperson);
      formData.append("designation", values.designation);
      formData.append(
        "secondary_contact_person",
        values.secondarycontactperson,
      );
      formData.append("secondary_designation", values.secondaryDesignation);
      formData.append("secondary_contact_email", values.secondaryEmail);
      formData.append("secondary_contact_phone", values.secondaryPhone);

      const newDocuments = documents.filter((doc) => doc.file);
      newDocuments.forEach((doc) => {
        formData.append("documents", doc.file);
      });
      if (newDocuments.length > 0) {
        formData.append(
          "document_categories",
          JSON.stringify(newDocuments.map((doc) => doc.category)),
        );
        formData.append(
          "document_expiry_dates",
          JSON.stringify(newDocuments.map((doc) => doc.expiryDate || null)),
        );
      }

      const response = await updateCompanyDetails(formData);

      if (Math.floor(response?.status / 100) !== 2) {
        return message.error(
          getErrorMessage(response?.data, "Unexpected response from server."),
        );
      }

      notification.success({
        message: "Updated",
        description: response?.data?.message || "Profile updated successfully",
      });

      if (existingData) {
        const updated = response.data?.data || {};

        setExistingData((prev) => ({
          ...prev,
          legal_name: updated.legal_name,
          registration_number: updated.registration_number,
          contact_email: updated.contact_email,
          contact_phone: updated.contact_phone,
          registration_contact_phone: updated.registration_contact_phone,
          address: updated.address,
          currency: updated.currency,
          date_incorporated: updated.date_incorporated,
          primary_contact_person: updated.primary_contact_person,
          designation: updated.designation,
          secondary_contact_person: updated.secondary_contact_person,
          secondary_designation: updated.secondary_designation,
          secondary_contact_email: updated.secondary_contact_email,
          secondary_contact_phone: updated.secondary_contact_phone,
          assigned_modules: updated.assigned_modules,
          vehicle_limit: updated.vehicle_limit,
          documents: updated.documents,
          navixy_api_keys: updated.navixy_api_keys || [],
        }));
        setDocuments((updated.documents || []).map(mapDocument));
        setIsEditing(false);
      } else {
        navigate("/profile-manager", {
          state: {
            updatedUser: {
              ...response.data,
              avatar,
              selectedModule,
              documents,
              vehicles,
            },
          },
        });
      }
    } catch (err) {
      message.error(
        getErrorMessage(err?.response?.data, "Failed to update profile."),
      );
    }
  };
  return (
    <div className="sign-up edit-sign-up">
      <div className="signup-card">
        {existingData && (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              marginBottom: 16,
            }}
          >
            {canEdit && !isEditing && (
              <Button onClick={handleEdit} icon={<EditOutlined />}>
                Edit Details
              </Button>
            )}
          </div>
        )}
        <Form form={form} layout="vertical">
          <div className="row">
            <div className="profile-card">
              <div className="profile-header">
                <div className="btn">
                  <Button
                    type="default"
                    className="upload-btn"
                    onClick={handleButtonClick}
                    disabled={existingData && !isEditing}
                  >
                    Upload Photo
                  </Button>
                </div>
                <input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />

                <div className="profile-avatar">
                  {avatar ? (
                    <img
                      src={avatar}
                      alt="avatar"
                      style={{
                        width: "100%",
                        height: "100%",
                        borderRadius: "50%",
                      }}
                    />
                  ) : (
                    "T"
                  )}
                </div>
              </div>
              <div className="profile-body"></div>
            </div>
          </div>
          <div>
            <div>
              <InputCard
                title="Registration Information"
                fields={registrationInformation.map((field) => ({
                  ...field,
                  disabled: [
                    "contactNumber",
                    "registrationNumber",
                    "registrationDate",
                  ].includes(field.name)
                    ? true
                    : !isEditing,
                }))}
              />
            </div>
            <div>
              <InputCard
                title="Contact Information"
                fields={contactInfoFields.map((field) => ({
                  ...field,
                  disabled: field.name === "businessemail" ? true : !isEditing,
                }))}
              />
            </div>
            <div>
              <InputCard
                title="Secondary Contact Information"
                fields={secondaryContactInformationFields.map((field) => ({
                  ...field,
                  disabled: !isEditing,
                }))}
              />
            </div>
            <div>
              <Tooltip
                title={
                  existingData && isEditing
                    ? "Modules can't be edited here. Go to the Subscription Payments page to upgrade or change your modules."
                    : undefined
                }
              >
                <div>
                  <ModuleSelctorCards
                    options={moduleOptions}
                    selectedId={
                      existingData
                        ? undefined
                        : selectedModule?.id || selectedModule
                    }
                    selectedIds={
                      existingData ? moduleOptions.map((m) => m.id) : undefined
                    }
                    multiple={!!existingData}
                    onChange={existingData ? undefined : handleSelection}
                    showVehicleInput={true}
                    vehicleCount={vehicles}
                    onVehicleChange={setVehicles}
                    allowSelection={!existingData && isEditing}
                    showFooter={true}
                    footerLabel={isAnnual ? "Annual Rate" : "Monthly Rate"}
                    headerExtra={
                      existingData && (
                        <Radio.Group
                          optionType="button"
                          buttonStyle="solid"
                          className="custom-segmented-control"
                          value={pricingPeriod}
                          onChange={(e) => setPricingPeriod(e.target.value)}
                          options={PRICING_TABS.map((tab) => ({
                            label: tab.replace(" Pricing", ""),
                            value: tab,
                            disabled:
                              !!planPricingPeriod && tab !== planPricingPeriod,
                          }))}
                        />
                      )
                    }
                  />
                </div>
              </Tooltip>
            </div>

            <div>
              <GenericDocumentManagerTable
                showUploadOnly={true}
                setDocuments={setDocuments}
                initialDocuments={documents}
                disabled={!isEditing}
                documentTypes={documentCategories}
                onDeleteDocument={async (doc) => {
                  if (!doc?.id) return;

                  const res = await deleteCompanyDocument(doc.id);

                  if (Math.floor(res?.status / 100) !== 2) {
                    throw new Error(
                      getErrorMessage(res?.data, "Failed to delete document."),
                    );
                  }
                }}
              />
            </div>
            <div></div>
          </div>
          {(!existingData || (canEdit && isEditing)) && (
            <div className="btns-row">
              <Button children="Cancel" onClick={handleCancel} />
              <Button
                children="Save Changes"
                onClick={() => handleSubmit()}
                className="save-btn"
              />
            </div>
          )}
        </Form>
      </div>
    </div>
  );
};

export default CompanyDetail;
